import { Component, EventEmitter, Output, TemplateRef, ViewChild } from "@angular/core";
import { NgbModalRef, NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Helper } from "src/app/core/helpers/helper";
import { PaymentService } from "../../services/payment.service";

declare let stripe: any;
declare let elements: any;

@Component({
  selector: "app-add-new-card",
  templateUrl: "./add-new-card.component.html",
  styleUrl: "./add-new-card.component.scss",
  standalone: false
})
export class AddNewCardComponent {
  modalRef!: NgbModalRef;
  modalConfig: any = { centered: true, backdrop: "static" };
  paymentGateway: any;
  paymentIntent: any;
  cardNumber: any;
  cardExpiry: any;
  cardCvc: any;
  corporateDetails: any;

  @ViewChild("template", { static: true }) template!: TemplateRef<any>;
  @Output() cardUpdated = new EventEmitter();

  constructor(public helper: Helper, private modalService: NgbModal, private paymentService: PaymentService) {}

  /**
   * Opens the add driver modal.
   * Initializes card elements and fetches corporate details.
   *
   * @param paymentGateway The payment gateway details.
   * @param intent The payment intent details.
   */
  openModal(paymentGateway: any, intent: any): void {
    this.modalRef = this.modalService.open(this.template, this.modalConfig);
    this.paymentGateway = paymentGateway;
    this.paymentIntent = intent;
    this._initCardElement();
    this.getCorporateDetails();

    this.modalRef.result.then(
      () => this.closeModal(),
      () => this.closeModal()
    );
  }

  /**
   * Initializes Stripe card elements for adding a new card.
   */
  _initCardElement(): void {
    this.helper.loadStripe(this.paymentGateway?.credential?.publicKey).then(() => {
      if (elements._elements.length) {
        elements._elements = [];
      }
      const createElement = (type: any, options: any, mountId: any): any => {
        const mode = document.documentElement.getAttribute("data-bs-theme");

        const style = {
          base: {
            color: mode === "dark" ? "#ced4da" : "#212529" // White text for dark mode, black for light mode
          }
        };

        const element = elements.create(type, { ...options, style });

        element.mount(mountId);
        element.on("change", handleChange(`${mountId.replace("#cc-", "")}-errors`));
        return element;
      };

      const handleChange =
        (errorId: any): any =>
        (event: any): void => {
          const displayError: any = document.getElementById(errorId);
          displayError.textContent = event.error ? event.error.message : "";
        };

      this.cardNumber = createElement("cardNumber", { placeholder: "xxxx xxxx xxxx xxxx" }, "#cc-number");
      this.cardExpiry = createElement("cardExpiry", {}, "#cc-expiration");
      this.cardCvc = createElement("cardCvc", { placeholder: "xxx" }, "#cc-cvv");
    });
  }

  /**
   * Fetches corporate details and assigns them to the component.
   */
  getCorporateDetails(): void {
    this.helper.getDetails.subscribe((res) => {
      this.corporateDetails = res;
    });
  }

  /**
   * Saves the new card using Stripe and updates the backend.
   */
  async saveCard(): Promise<void> {
    // Disable button to prevent multiple submissions
    this.helper.showBtnLoader[1] = true;
    const { error } = await stripe.createToken(this.cardNumber);

    if (error) {
      this.helper.showBtnLoader[1] = false; // Re-enable the button if there's an error
      return;
    }

    if (this.paymentIntent) {
      const clientSecret = this.paymentIntent.client_secret;

      const result = await stripe.handleCardSetup(clientSecret, this.cardNumber);

      if (result.error) {
        this.cardNumber.clear();
        this.helper.showBtnLoader[1] = false; // Re-enable the button after the error
        return;
      }

      const paymentMethod = result.setupIntent.payment_method;
      const json = {
        countryId: this.corporateDetails.countryId,
        paymentMethod: paymentMethod
      };

      this.paymentService.addNewCard(this.paymentGateway.type, json).subscribe(() => {
        this.cardUpdated.emit();
        this.closeModal();
        this.cardNumber.clear();
        this.helper.showBtnLoader[1] = false; // Re-enable the button after completion
      });
    } else {
      this.helper.showBtnLoader[1] = false; // Re-enable the button after completion
    }
  }

  /**
   * Closes the modal and resets the component state.
   */
  closeModal(): void {
    this.modalRef?.dismiss("Cross click"); // Close modal with reason 'Cross click'
  }
}
